import React from 'react';
import { Music, Timer, Mic, Cloud } from 'lucide-react';

export const LandingFeatures: React.FC = () => {
  return (
    <section
      id="features"
      className="max-w-[1248px] mx-auto px-5 md:px-6 pt-14 pb-6 md:py-16 flex flex-col gap-6 md:gap-8"
    >
      <div className="max-w-[640px]">
        <h2 className="text-[26px] md:text-4xl font-extrabold leading-[1.4] md:leading-[1.3] m-0 text-base-content">
          چهار ابزار، یک ایستگاه تمرین
        </h2>
        <p className="hidden md:block mt-2 text-sm md:text-base leading-[1.9] md:leading-[1.9] text-base-content/60 m-0">
          برای گیتار الکتریک، کلاسیک، آکوستیک و باس.
        </p>
      </div>

      <div
        data-testid="landing-features-grid"
        className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4"
      >
        {/* Feature 1: Tab Player */}
        <article className="bg-base-200 border border-base-300 rounded-[18px] md:rounded-[20px] p-5 md:p-7 flex flex-row md:flex-col gap-3.5 md:gap-3">
          <span className="size-10 md:size-11 shrink-0 rounded-xl bg-primary/14 text-primary-soft flex items-center justify-center">
            <Music className="size-5 md:size-5.5" aria-hidden="true" />
          </span>
          <div>
            <h3 className="text-base md:text-xl font-bold m-0 text-base-content">
              تبلچرخوان چندترکه
            </h3>
            <p className="mt-1.5 md:mt-2 text-sm md:text-[15px] leading-[1.8] md:leading-[1.9] text-base-content/60 m-0">
              <span className="hidden md:inline">
                فایل‌های <span dir="ltr">GP3</span> تا <span dir="ltr">GP7</span>. سرعت از ۱۰ تا ۱۰۰ درصد بدون تغییر زیر و بمی، لوپ روی میزان‌ها و سولو یا بی‌صدا کردن هر ساز.
              </span>
              <span className="md:hidden">
                سرعت ۱۰ تا ۱۰۰ درصد بدون تغییر زیر و بمی، لوپ و میکسر ترک‌ها.
              </span>
            </p>
          </div>
        </article>

        {/* Feature 2: Routine & Focus */}
        <article className="bg-base-200 border border-base-300 rounded-[18px] md:rounded-[20px] p-5 md:p-7 flex flex-row md:flex-col gap-3.5 md:gap-3">
          <span className="size-10 md:size-11 shrink-0 rounded-xl bg-accent/14 text-accent-soft flex items-center justify-center">
            <Timer className="size-5 md:size-5.5" aria-hidden="true" />
          </span>
          <div>
            <h3 className="text-base md:text-xl font-bold m-0 text-base-content">
              روتین و حالت تمرکز
            </h3>
            <p className="mt-1.5 md:mt-2 text-sm md:text-[15px] leading-[1.8] md:leading-[1.9] text-base-content/60 m-0">
              <span className="hidden md:inline">
                جلسه را به بلوک‌های گرم‌کردن، تکنیک و قطعه تقسیم کنید. صفحه روشن می‌ماند و در پایان هر بلوک بالاترین تمپوی تمیز ثبت می‌شود.
              </span>
              <span className="md:hidden">
                بلوک‌های زمان‌بندی‌شده، صفحهٔ همیشه روشن و ثبت تمپوی تمیز.
              </span>
            </p>
          </div>
        </article>

        {/* Feature 3: Chromatic Tuner */}
        <article className="bg-base-200 border border-base-300 rounded-[18px] md:rounded-[20px] p-5 md:p-7 flex flex-row md:flex-col gap-3.5 md:gap-3">
          <span className="size-10 md:size-11 shrink-0 rounded-xl bg-success/14 text-success-soft flex items-center justify-center">
            <Mic className="size-5 md:size-5.5" aria-hidden="true" />
          </span>
          <div>
            <h3 className="text-base md:text-xl font-bold m-0 text-base-content">
              تیونر کروماتیک
            </h3>
            <p className="mt-1.5 md:mt-2 text-sm md:text-[15px] leading-[1.8] md:leading-[1.9] text-base-content/60 m-0">
              <span className="hidden md:inline">
                تشخیص بلادرنگ نت با میکروفون، قفل کوک در محدودهٔ <span dir="ltr">±3</span> سنت و کوک‌های آماده از استاندارد تا <span dir="ltr">Drop A#</span> و هفت‌سیم.
              </span>
              <span className="md:hidden">
                قفل کوک در <span dir="ltr">±3</span> سنت و کوک‌های آماده تا هفت‌سیم.
              </span>
            </p>
          </div>
        </article>

        {/* Feature 4: Offline always */}
        <article
          id="offline"
          className="bg-base-200 border border-base-300 rounded-[18px] md:rounded-[20px] p-5 md:p-7 flex flex-row md:flex-col gap-3.5 md:gap-3"
        >
          <span className="size-10 md:size-11 shrink-0 rounded-xl bg-info/14 text-info-soft flex items-center justify-center">
            <Cloud className="size-5 md:size-5.5" aria-hidden="true" />
          </span>
          <div>
            <h3 className="text-base md:text-xl font-bold m-0 text-base-content">
              آفلاین، همیشه
            </h3>
            <p className="mt-1.5 md:mt-2 text-sm md:text-[15px] leading-[1.8] md:leading-[1.9] text-base-content/60 m-0">
              <span className="hidden md:inline">
                تبلچرها، روتین‌ها و سوابق تمرین روی دستگاه ذخیره می‌شوند و پس از اتصال خودکار همگام می‌شوند. نصب روی ویندوز، مک و اندروید.
              </span>
              <span className="md:hidden">
                همه‌چیز روی دستگاه؛ همگام‌سازی خودکار پس از اتصال.
              </span>
            </p>
          </div>
        </article>
      </div>
    </section>
  );
};
